import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TipoMensajeSalidaFlujoTramitePension } from '@constants/business.constant';
import { ModalService } from '@services/modal/modal.service';
import { PiConfirmacionCardComponent } from '../../components/pi-confirmacion-card/pi-confirmacion-card.component';
import { PiExclusionesCardComponent } from '../../components/pi-exclusiones-card/pi-exclusiones-card.component';
import { PiHeroCardComponent } from '../../components/pi-hero-card/pi-hero-card.component';
import { PiModalQueEsPensionComponent } from '../../components/pi-modal-que-es-pension/pi-modal-que-es-pension.component';
import { PiModalRevisaPasosComponent } from '../../components/pi-modal-revisa-pasos/pi-modal-revisa-pasos.component';
import { PiRequisitosCardComponent } from '../../components/pi-requisitos-card/pi-requisitos-card.component';
import { DsGenericErrorComponent } from '@shared/components/design-system/generic-error/generic-error.component';
import { GenericErrorOps } from '@shared/components/design-system/generic-error/interfaces/generic-error-ops.interface';
import {
  CUENTA_OBLIGATORIA,
  MESSAGE,
  SESSION_TRAMITE_PENSION_INVALIDEZ_ERROR_GENERAL_INICIO_REINTENTOS_RESTANTES,
  TRAMITE_PENSION_INVALIDEZ_ERROR_GENERAL_INICIO_MAX_REINTENTOS,
} from '@constants/tramite-pension-invalidez';
import { Util } from '@shared/common/utils/util';
import { StorageService } from '@services/storage/storage.service';
import { DsSkeletonComponent } from '@customdesignsystem';
import { GtmService } from '@services/gtm/gtm.service';
import { PiPreguntaPaso0Component } from '../../components/pi-pregunta-paso-0/pi-pregunta-paso-0.component';
import { TramitePensionServiceInvalidez } from '../../service/tramite-pension-invalidez.service';
import { firstValueFrom } from 'rxjs';
import { REQUEST_ERROR, ROUTING_TRAMITE_PENSION_INVALIDEZ } from '@constants/url.constant';
import { ActivatedRoute, Router } from '@angular/router';
import { ACCOUNTS } from '@constants/data-service.constant';
import { InvalidezStepperService } from '../../service/invalidez-stepper.service';
import { TramitePensionService } from 'src/app/tramite-pension/services/tramite-pension.service';
import { BeneficiariosValidadorService } from 'src/app/tramite-pension/services/beneficiarios-validador.service';
import { Beneficiary } from '@shared/common/interfaces/beneficiario.interface';
import {
  MENSAJES_LISTA_ERRORES_BENEFICIARIOS,
  TipoErrorValidacionBeneficiarios,
  TP_PERSONAL_DATA,
} from '@constants/tramite-pension-constant';

/**
 * Paso 0 — Solicitud de Pensión por Invalidez.
 * La página solo usa wrappers de pensionados; esos wrappers consumen el DS.
 */
@Component({
  selector: 'app-invalidez-home',
  standalone: true,
  imports: [
    CommonModule,
    PiHeroCardComponent,
    PiRequisitosCardComponent,
    PiExclusionesCardComponent,
    PiConfirmacionCardComponent,
    DsGenericErrorComponent,
    DsSkeletonComponent,
    PiPreguntaPaso0Component,    
  ],
  templateUrl: './invalidez-home.component.html',
  styleUrls: ['./invalidez-home.component.scss'],
})
export class InvalidezHomeComponent implements OnInit {
  isError: boolean | null = null;
  errorOps: GenericErrorOps | null = null;
  attempt: boolean = false;
  loading: boolean = true;
  iniciandoSolicitud: boolean = false;
  preguntaPrePaso0: boolean = true;

  constructor(
    private readonly modalService: ModalService,
    private readonly util: Util,
    private readonly storageService: StorageService,
    private readonly gtmService: GtmService,
    private readonly tramitePensionService: TramitePensionServiceInvalidez,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly tramiteStepper: InvalidezStepperService,
    private readonly datosTramite: TramitePensionService,
    private readonly beneficiariosValidador: BeneficiariosValidadorService,
  ) {
    const state = this.router.getCurrentNavigation?.()?.extras?.state;
    if (state?.['mostrarInicioTramite'] === true) {
      this.preguntaPrePaso0 = false;
    }
  }

  async ngOnInit(): Promise<void> {
    await this.validacionEdadJubilacion();
  }

  private async validacionEdadJubilacion() {
    try {
      const personalData = await this.storageService.get('basic_client_info');

      const tieneEdadLegalDePension = this.util.tieneEdadLegalDePension(
        personalData?.birthDate ?? "",
        personalData?.gender === 'M' ? 'MASCULINO' : 'FEMENINO'
      );

      this.isError = tieneEdadLegalDePension;
      this.errorOps = tieneEdadLegalDePension ? MESSAGE.ERROR_GENERAL_LIMITE_EDAD : null;

    } finally {
      this.loading = false;
    }
  }


  onQueEsPension(): void {
    this.gtmService.trackClick('link_que_es_pension_invalidez', '¿Qué es la Pensión por Invalidez?', 'link_interno', 'modal');
    void this.modalService.openCustomModal({
      component: PiModalQueEsPensionComponent,
      cssClass: ['generic-modal', 'pi-modal-que-es-pension'],
      backdropDismiss: true,
    });
  }

  onRevisaPasos(): void {
    this.gtmService.trackClick('boton_revisa_los_pasos', 'Revisa los pasos', 'boton_secundario', 'modal');
    void this.modalService.openCustomModal({
      component: PiModalRevisaPasosComponent,
      cssClass: ['generic-modal', 'pi-modal-revisa-pasos'],
      backdropDismiss: true,
    });
  }

  onAgendarVideoAtencion(): void {
    this.gtmService.trackClick('link_videoatencion', 'Video Atención', 'link_interno', 'modal');
    void this.modalService.openGenericModalMensajeSalida(
      TipoMensajeSalidaFlujoTramitePension.VideoAtencion
    );
  }

  onAbrirInfoImportante(): void {
    this.gtmService.trackClick('link_informacion_importante', 'Información importante', 'link_interno', 'modal');
    // Maquetación: enlace informativo pendiente de contenido.
  }

  async onIniciarSolicitud(): Promise<void> {
    if (this.iniciandoSolicitud) {
      return;
    }

    this.iniciandoSolicitud = true;
    try {



      this.gtmService.trackClick('boton_iniciar_solicitud', 'Iniciar Solicitud', 'boton_primario', '/paso1');

      const personalData = await firstValueFrom(this.datosTramite.getPersonalData());

      await this.storageService.set(TP_PERSONAL_DATA, personalData);


      if (personalData?.codigoPaisNacionalidad !== "CL") {
        this.enviarAPaginaDeErrorPorValidacion(MESSAGE.ERROR_BENEFICIARIO_CON_INVALIDEZ);
        return;
      }

      const tieneCCO = await this.validaProductoCliente();
      if (!tieneCCO) {
        this.tramitePensionService.setError(MESSAGE.ERROR_NO_AHORROS_OBLIGATORIOS_AFP_CAPITAL);
        this.router.navigateByUrl(
          `/${ROUTING_TRAMITE_PENSION_INVALIDEZ.BASE_URL}/${REQUEST_ERROR}`
        );
        return;
      }


      const validaciones: any = await firstValueFrom(
        this.tramitePensionService.obtenerValidacionesTramitePensionInvalidez()
      );

      if (validaciones?.status === "400" && validaciones?.tipoValidacion === "estadoCivilNoPermitido") {
        this.enviarAPaginaDeErrorPorValidacion(
          MESSAGE.ERROR_NO_CUMPLE_CON_ESTADO_CIVIL_VALIDACION
        );
        return;
      }

      const codigoStatus = String(
        validaciones?.codigoStatus ??
        validaciones?.codigoRetorno ??
        validaciones?.data?.codigoStatus ??
        validaciones?.data?.codigoRetorno ??
        ""
      );

      if (codigoStatus === "AU05") {
        this.enviarAPaginaDeErrorPorValidacion(
          MESSAGE.ERROR_SIN_SALDOS_OBLIGATORIOS_AFP_CAPITAL
        );
        return;
      }

      if (codigoStatus === "AU04" || codigoStatus === "AU06" || codigoStatus === "AU10") {
        this.enviarAPaginaDeErrorPorValidacion(
          MESSAGE.VALIDACION_SOLICITUD_EN_CURSO
        );
        return;
      }

      if (codigoStatus === "AU09") {
        this.enviarAPaginaDeErrorPorValidacion(
          MESSAGE.VALIDACION_CLIENTE_FALLECIDO
        );
        return;
      }

      if (validaciones?.status === "400") {
        this.enviarAPaginaDeErrorGeneral();
        return;
      }

      const beneficiariosOk = await this.validarBeneficiariosAntesDeContinuar();
      if (!beneficiariosOk) {
        return;
      }

      // Navegación al paso 1 del trámite de invalidez.
      this.tramiteStepper.setStepCurrent(1);
      this.router.navigate(
        [`/${ROUTING_TRAMITE_PENSION_INVALIDEZ.BASE_URL}/${ROUTING_TRAMITE_PENSION_INVALIDEZ.REQUEST}`],
        { relativeTo: this.activatedRoute }
      );

    } catch (error) {
      console.error("Error al iniciar el trámite de invalidez", error);
      this.enviarAPaginaDeErrorGeneral();
    } finally {
      this.iniciandoSolicitud = false;
    }
  }

  private enviarAPaginaDeErrorPorValidacion(mensaje: any): void {
    this.tramitePensionService.setError(mensaje);
    this.router.navigateByUrl(
      `/${ROUTING_TRAMITE_PENSION_INVALIDEZ.BASE_URL}/${REQUEST_ERROR}`
    );
  }

  private enviarAPaginaDeErrorGeneral(): void {
    const key = SESSION_TRAMITE_PENSION_INVALIDEZ_ERROR_GENERAL_INICIO_REINTENTOS_RESTANTES;
    const raw = sessionStorage.getItem(key);
    let restantes =
      raw === null
        ? TRAMITE_PENSION_INVALIDEZ_ERROR_GENERAL_INICIO_MAX_REINTENTOS
        : Number.parseInt(raw, 10);
    if (Number.isNaN(restantes)) {
      restantes = TRAMITE_PENSION_INVALIDEZ_ERROR_GENERAL_INICIO_MAX_REINTENTOS;
    }

    // Cada fallo consume un intento; al 3.er fallo (restantes llega a 0) → pantalla sin Reintentar.
    restantes = Math.max(0, restantes - 1);
    sessionStorage.setItem(key, String(restantes));

    const errorMsg =
      restantes <= 0
        ? MESSAGE.ERROR_GENERAL_INICIAR_TRAMITE_SIN_REINTENTOS
        : MESSAGE.ERROR_GENERAL_INICIAR_TRAMITE;

    this.tramitePensionService.setError(errorMsg);
    this.router.navigateByUrl(
      `/${ROUTING_TRAMITE_PENSION_INVALIDEZ.BASE_URL}/${REQUEST_ERROR}`
    );
  }

  onContinuarPaso0(): void {
    this.preguntaPrePaso0 = false;

    this.gtmService.handleStepTransition({
      paso_numero: '0b',
      paso_nombre: 'inicio_solicitud',
      event_name: 'tramite_invalidez'
    });

    this.gtmService.trackGenericEvent('view', 'Vista de página', 'Vista de página', '-', this.router.url);
  }

  private async validarBeneficiariosAntesDeContinuar(): Promise<boolean> {
    const personalData = await firstValueFrom(this.datosTramite.getPersonalData());
    const beneficiaries = await this.obtenerBeneficiarios();
    const validacion = this.beneficiariosValidador.validarBeneficiarios(
      beneficiaries,
      personalData?.estadoCivil ?? "",
      personalData?.apellidoPaterno ?? ""
    );
    const errorMandatorio = validacion.erroresMandatorios[0];
    if (errorMandatorio === TipoErrorValidacionBeneficiarios.ERROR_SERVICIO) {
      this.enviarAPaginaDeErrorGeneral();
      return false;
    }
    if (errorMandatorio === TipoErrorValidacionBeneficiarios.BENEFICIARIO_CON_INVALIDEZ) {
      this.enviarAPaginaDeErrorPorValidacion(MESSAGE.ERROR_BENEFICIARIO_CON_INVALIDEZ);
      return false;
    }

    if (validacion.erroresAgrupados.length === 0) {
      return true;
    }

    const lista = MENSAJES_LISTA_ERRORES_BENEFICIARIOS
      .filter((item) => validacion.erroresAgrupados.includes(item.tipo))
      .map((item) => item.mensaje);

    this.enviarAPaginaDeErrorPorValidacion({
      ...MESSAGE.ERROR_BENEFICIARIOS_VALIDACION_AGRUPADA,
      lista,
    });
    return false;
  }

  private async obtenerBeneficiarios(): Promise<Beneficiary[] | null> {
    try {
      return await firstValueFrom(this.datosTramite.getBeneficiaries());
    } catch {
      return null;
    }
  }

  private async validaProductoCliente(): Promise<boolean> {
    const listaProductos = await this.storageService.get(ACCOUNTS);

    if (!Array.isArray(listaProductos) || listaProductos.length === 0) {
      return false;
    }

    return listaProductos.some((producto: any) => {
      const accountId = producto?.accountConfiguration?.id ?? producto?.id ?? "";
      return accountId === CUENTA_OBLIGATORIA;
    });
  }
}
